import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Markdown } from '@/shared/components/markdown/Markdown'
import {
  fetchMyRatings,
  fetchReleases,
  formatReleaseDate,
  stripLeadingTitle,
  type ReleaseNote as Note,
} from '@/features/releases/releases'
import { RatingStars } from '@/features/releases/RatingStars'
import { usePortalSession } from '@/features/releases/usePortalSession'

type Status = 'pending' | 'ready' | 'empty'

/**
 * Sección "Actualizaciones" de la landing (goal portal-notas-de-version-en-
 * vivo, decisión 5): la nota de versión MÁS RECIENTE, justo después de "Lo
 * último", sin entrada en Header/Footer. Ojo: News ya usa id="novedades" —
 * esta sección usa id="actualizaciones".
 *
 * Carga DIFERIDA (decisión 7): cero peticiones en el load inicial; el fetch se
 * dispara con IntersectionObserver cuando el usuario se ACERCA a la sección, y
 * hasta entonces un placeholder de altura estable reserva el hueco (sin CLS).
 * El renderer Markdown ya viene en el chunk inicial (lo importan estáticamente
 * LegalPage/NewsDetailPage vía el router), así que no hay chunk que diferir.
 * Sin notas la sección se oculta sola (patrón News).
 */
export function ReleaseNotes() {
  const { t, i18n } = useTranslation()
  const { ensureSession } = usePortalSession()
  const sectionRef = useRef<HTMLElement | null>(null)
  const startedRef = useRef(false)
  const [status, setStatus] = useState<Status>('pending')
  const [note, setNote] = useState<Note | null>(null)
  const [myScore, setMyScore] = useState<number | null>(null)

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    let active = true

    const load = async () => {
      if (startedRef.current) return
      startedRef.current = true
      try {
        const { items } = await fetchReleases({ page: 1, pageSize: 1 })
        if (!active) return
        if (!items.length) {
          setStatus('empty')
          return
        }
        const latest = items[0]
        setNote(latest)
        setStatus('ready')
        // El voto propio solo si hay sesión (hidratación silenciosa; null =
        // anónimo, la landing nunca se rompe por esto).
        const token = await ensureSession()
        if (!token || !active) return
        const mine = await fetchMyRatings([latest.id])
        if (!active) return
        setMyScore(
          mine.find((m) => m.releaseNoteId === latest.id)?.score ?? null,
        )
      } catch {
        if (active) setStatus('empty')
      }
    }

    // Sin IntersectionObserver (navegadores viejos) se carga directo: mejor
    // una petición de más que una sección muerta.
    if (typeof IntersectionObserver === 'undefined') {
      void load()
      return () => {
        active = false
      }
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void load()
          observer.disconnect()
        }
      },
      // rootMargin generoso: el fetch arranca ANTES de que la sección entre
      // en pantalla para que el usuario no vea el esqueleto al llegar.
      { rootMargin: '600px 0px' },
    )
    observer.observe(el)
    return () => {
      active = false
      observer.disconnect()
    }
  }, [ensureSession])

  if (status === 'empty') return null

  return (
    <section id="actualizaciones" ref={sectionRef} className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 lg:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 lg:mb-16 gap-4 reveal">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              {t('releases.badge')}
            </span>
            <h2 className="text-3xl lg:text-5xl font-bold tracking-tight mt-3">
              {t('releases.title')}
            </h2>
          </div>
          <Link
            to="/actualizaciones"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-primary"
          >
            {t('releases.seeAll')} <ArrowUpRight className="size-4" />
          </Link>
        </div>

        {status === 'pending' || !note ? (
          // Placeholder de altura ESTABLE mientras llega el fetch diferido:
          // reserva el hueco de la tarjeta para que el CLS quede en cero.
          <div
            aria-hidden="true"
            className="min-h-[26rem] rounded-3xl border border-black/5 bg-neutral-50 p-8 lg:p-10"
          >
            <div className="h-4 w-48 animate-pulse rounded bg-neutral-200" />
            <div className="mt-6 h-8 w-2/3 animate-pulse rounded bg-neutral-200" />
            <div className="mt-8 space-y-3">
              <div className="h-4 w-full animate-pulse rounded bg-neutral-100" />
              <div className="h-4 w-5/6 animate-pulse rounded bg-neutral-100" />
              <div className="h-4 w-4/6 animate-pulse rounded bg-neutral-100" />
            </div>
          </div>
        ) : (
          /* Sin clase `reveal` a propósito: el article sustituye a un
             placeholder YA visible — animarlo desde opacity 0 haría
             parpadear un hueco que el usuario ya vio pintado. */
          <article className="min-h-[26rem] rounded-3xl border border-black/5 bg-white p-8 shadow-sm lg:p-10">
            <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
                {note.product}
              </span>
              <span className="text-xs font-semibold text-muted-foreground">
                {t('releases.version', { version: note.version })}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatReleaseDate(note.releasedAt, i18n.language)}
              </span>
            </div>

            {/* Cuerpo colapsado: altura máxima + degradado si excede (el
                degradado blanco es invisible sobre contenido corto). */}
            <div className="relative mt-4 max-h-72 overflow-hidden">
              <Markdown>{stripLeadingTitle(note.body, note.version)}</Markdown>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent" />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-black/5 pt-6">
              <RatingStars
                releaseNoteId={note.id}
                avgRating={note.avgRating}
                ratingsCount={note.ratingsCount}
                myScore={myScore}
              />
              <Link
                to="/actualizaciones"
                className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-2.5 text-sm font-bold text-background transition-colors hover:bg-primary"
              >
                {t('releases.seeMore')}
                <ArrowUpRight className="size-4" />
              </Link>
            </div>
          </article>
        )}
      </div>
    </section>
  )
}
